import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { View, ActivityIndicator, RefreshControl, Platform } from 'react-native';
import { useBottomSheetScrollableCreator } from '@gorhom/bottom-sheet';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FlashList } from '@shopify/flash-list';

import { MemberListCard } from './MemberListCard';
import { PremiumUpgradeCard } from './PremiumUpgradeCard';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { PostsService } from '~/services/domain/PostsService';
import {
  PageParam,
  getNextPageSkipOrCursor,
  buildPaginationParams,
} from '@/hooks/usePaginationUtils';
import { CommentsService } from '~/services/domain/CommentsService';
import { PhotosService } from '~/services/domain/PhotosService';
import { MembersService } from '~/services/domain/MembersService';
import { MembersView, Member } from '@/models/Member';
import { MessageView } from '@/components/ui/MessageView';
import { ErrorInListView } from '@/components/ui/ErrorInListView';
import { UserMultiple02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { useTranslation } from 'react-i18next';
import { useIsFocused } from 'expo-router/react-navigation';
import {
  IContextSheetContentProps,
  ISheetConfig,
  DRAWER_SCROLL_CLASSES,
} from '@/components/ui/ContextSheet';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { Post } from '@/models/Post';
import { Comment } from '@/models/Comment';
import { Photo } from '@/models/Photo';
import { useInfiniteListUtils, createSkeletonItems } from '@/hooks/useInfiniteListUtils';
import { useFlashListOptimizer } from '@/hooks/useFlashListOptimizer';

function MemberListRowSeparator() {
  return <View className="h-5" />;
}

type ListType =
  | 'post-likes'
  | 'comment-likes'
  | 'photo-likes'
  | 'followers'
  | 'following'
  | 'blocked'
  | 'photo-unlock-authorized';

interface MemberListSheetProps extends IContextSheetContentProps {
  payload?: {
    listType: ListType;
    post?: Post;
    comment?: Comment;
    photo?: Photo;
    member?: Member;
    actionMode?: 'follow' | 'unblock' | 'remove-unlock';
  };
}

export function MemberListSheet({ payload, setTitleCount }: MemberListSheetProps) {
  const { t } = useTranslation();
  const flashListRef = useRef<React.ComponentRef<typeof FlashList<any>>>(null);
  const gorhomFlashListScroll = useBottomSheetScrollableCreator();
  const isScreenActive = useIsFocused();
  const insets = useSafeAreaInsets();
  const isLargeScreen = useIsLargeScreen();
  const { onEndReachedThreshold, pageSize } = useFlashListOptimizer({
    estimatedRowsOnScreen: { mobile: 13, desktop: 16 },
  });

  const listType = payload?.listType;
  const post = payload?.post;
  const comment = payload?.comment;
  const photo = payload?.photo;
  const member = payload?.member;
  const actionMode = payload?.actionMode ?? 'follow';

  // Generate query key based on list type
  const getQueryKey = () => {
    switch (listType) {
      case 'post-likes':
        return ['members', listType, post?.Id ?? 0];
      case 'comment-likes':
        return ['members', listType, comment?.Id ?? 0];
      case 'photo-likes':
        return ['members', listType, photo?.Id ?? 0];
      case 'followers':
        return ['members', listType, member?.Id ?? 0];
      case 'following':
        return ['members', listType, member?.Id ?? 0];
      case 'blocked':
        return ['members', listType];
      case 'photo-unlock-authorized':
        return ['members', listType];
      default:
        return ['members', 'unknown'];
    }
  };

  const queryKey = getQueryKey();

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    isPending,
    refetch,
    isStale,
  } = useInfiniteQuery({
    queryKey: queryKey,
    queryFn: async ({ pageParam }) => {
      const params = buildPaginationParams(pageParam, pageSize);

      switch (listType) {
        case 'post-likes':
          if (!post) throw new Error('Post data is missing');
          return await PostsService.getLikes(post, params);

        case 'comment-likes':
          if (!comment) throw new Error('Comment data is missing');
          return await CommentsService.getLikes(comment, params);

        case 'photo-likes':
          if (!photo) throw new Error('Photo data is missing');
          return await PhotosService.getPhotoLikes(photo.Id, params);

        case 'followers':
          return await MembersService.getFollowersMembers({
            ...params,
            memberId: member?.Id,
          });

        case 'following':
          return await MembersService.getFollowingMembers({
            ...params,
            memberId: member?.Id,
          });
        case 'blocked':
          return await MembersService.getBlockedMembers({ ...params });

        case 'photo-unlock-authorized':
          return await MembersService.getUnlockedMembers({ ...params });

        default:
          throw new Error('Invalid list type');
      }
    },
    initialPageParam: undefined as PageParam,
    getNextPageParam: getNextPageSkipOrCursor,
    select: useCallback((data: InfiniteData<MembersView, PageParam>) => {
      const items = data.pages.flatMap((page) => page.MembersBasic);
      const nonPremiumLimitReached = data.pages.some(
        (page) => page.NonPremiumLimitReached === true
      );
      return { ...data, items, nonPremiumLimitReached };
    }, []),
    gcTime: 0,
    refetchOnMount: true,
    enabled:
      !!listType &&
      ((listType === 'post-likes' && !!post) ||
        (listType === 'comment-likes' && !!comment) ||
        (listType === 'photo-likes' && !!photo) ||
        ((listType === 'followers' || listType === 'following') && !!member) ||
        listType === 'blocked' ||
        listType === 'photo-unlock-authorized'),
  });

  // Show skeleton if data is stale and refetching, or if pending
  const isInitialLoading = isPending || (isStale && isRefetching);

  // Set title count from first page's ItemCount when data changes
  useEffect(() => {
    if (setTitleCount && data?.pages?.[0]) {
      const apiCount = (data.pages[0] as any)?.ItemCount as number | undefined;
      if (typeof apiCount === 'number') {
        setTitleCount(apiCount);
      }
    }
  }, [setTitleCount, data?.pages?.[0]]);

  const { handleRefresh } = useInfiniteListUtils({
    queryKey: queryKey,
    flashListRef,
    disableRestoration: true, // Disable scroll position restoration since we want to start at top
  });

  // Estimate skeleton count based on available count data
  let skeletonCount = 6; // default
  if (listType === 'post-likes' && post?.LikesCount) {
    skeletonCount = Math.min(post.LikesCount, 6);
  } else if (listType === 'comment-likes' && comment?.LikesCount) {
    skeletonCount = Math.min(comment.LikesCount, 6);
  } else if (listType === 'photo-likes' && photo?.LikesCount) {
    skeletonCount = Math.min(photo.LikesCount, 6);
  }
  const skeletonItems = useMemo(() => createSkeletonItems(skeletonCount), [skeletonCount]);

  const listData = isInitialLoading ? skeletonItems : (data?.items ?? []);

  const renderItem = useCallback(
    ({ item: memberItem }: { item: any }) =>
      isInitialLoading ? (
        <MemberListCard />
      ) : (
        <MemberListCard member={memberItem} actionMode={actionMode} />
      ),
    [actionMode, isInitialLoading]
  );

  const keyExtractor = useCallback(
    (item: any, index: number) =>
      isInitialLoading || typeof item?.Id !== 'number' ? `s-${index}` : `member-${item.Id}`,
    [isInitialLoading]
  );

  const getItemType = useCallback((item: any) => {
    if (item == null || typeof item.Id !== 'number') {
      return 'skeleton';
    }
    return item.PlaceNameShortenedTwoElements ? 'member-with-location' : 'member-compact';
  }, []);

  const onEndReached = useCallback(() => {
    if (!isInitialLoading && hasNextPage && isScreenActive && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isInitialLoading, isScreenActive]);

  const listFooter = useMemo(
    () => (
      <View>
        {isFetchingNextPage && (
          <View className="m-4">
            <ActivityIndicator color="grey" />
          </View>
        )}
        {!isInitialLoading && data?.nonPremiumLimitReached && (
          <View className="mt-4">
            <PremiumUpgradeCard listType="members" />
          </View>
        )}
        <View style={{ height: Math.max(insets.bottom, 12) }} />
      </View>
    ),
    [data?.nonPremiumLimitReached, insets.bottom, isFetchingNextPage, isInitialLoading]
  );

  const listEmpty = useMemo(
    () => (
      <MessageView
        type="information"
        text={t('common.empty')}
        subText={
          listType === 'blocked'
            ? t('settings.privacy.emptyHelp.blocked')
            : listType === 'photo-unlock-authorized'
              ? t('settings.privacy.emptyHelp.photoUnlockAuthorized')
              : undefined
        }
        icon={UserMultiple02Icon}
        listEmpty={isLargeScreen ? true : 300}
      />
    ),
    [isLargeScreen, listType, t]
  );

  if (error) {
    return <ErrorInListView error={error} onRetry={() => refetch()} isRetrying={isRefetching} />;
  }

  return (
    <View className="min-h-0 flex-1" style={{ flex: 1 }}>
      <FlashList<any>
        ref={flashListRef}
        style={{ flex: 1 }}
        data={listData}
        extraData={`${actionMode}:${isInitialLoading ? 1 : 0}`}
        {...DRAWER_SCROLL_CLASSES}
        {...(!isLargeScreen ? { renderScrollComponent: gorhomFlashListScroll } : {})}
        renderItem={renderItem}
        getItemType={getItemType}
        keyExtractor={keyExtractor}
        onEndReached={onEndReached}
        onEndReachedThreshold={onEndReachedThreshold}
        ListFooterComponent={listFooter}
        refreshControl={
          isLargeScreen ? (
            <RefreshControl refreshing={isRefetching} onRefresh={handleRefresh} />
          ) : undefined
        }
        showsVerticalScrollIndicator={Platform.OS === 'web'}
        ItemSeparatorComponent={MemberListRowSeparator}
        ListEmptyComponent={listEmpty}
      />
    </View>
  );
}

MemberListSheet.sheetConfig = {
  largeScreenMode: 'drawer',
  size: 'sm',
  height: 'lg',
  additionalSnapPoints: ['90%'],
  flushBottom: true,
  scrollableContent: true,
  integratedNativeSheetScrollGestures: true,
  hideCloseButton: true,
} satisfies ISheetConfig;
